import { Error, ErrorTargets } from '../../../../models/response';

export enum CreateChallengeErrorCodes {
  // 400 - Bad request
  StartDateLaterThanFinishDate = 'StartDateLaterThanFinishDate',
  StartDateLessThanNow = 'StartDateLessThanNow',
  InvalidChallengeVisibility = 'InvalidChallengeVisibility',
  InvalidChallengeCreatorType = 'InvalidChallengeCreatorType',
  DurationTooLong = 'DurationTooLong',
  DurationTooShort = 'DurationTooShort',
  NotFound = 'NotFound',
  BadRequest = 'BadRequest',
  AccessTimeLimitMinutesCannotExceedDuration = 'AccessTimeLimitMinutesCannotExceedDuration',
  AccessTimeLimitMinutesTooLow = 'AccessTimeLimitMinutesTooLow',
  StudentIsNotTrainer = 'StudentIsNotTrainer',
  StudentDoesNotBelongInAcademy = 'StudentDoesNotBelongInAcademy',
  FieldLengthNotValid = 'FieldLengthNotValid',
  // 403 - Insufficient permissions
  InsufficientPermissions = 'InsufficientPermissions',
}

export declare namespace CreateChallengeError {
  type ErrorTypes =
    | StartDateLaterThanFinishDate
    | StartDateLessThanNow
    | InvalidChallengeVisibility
    | InvalidChallengeCreatorType
    | DurationTooLong
    | DurationTooShort
    | NotFound
    | BadRequest
    | AccessTimeLimitMinutesCannotExceedDuration
    | AccessTimeLimitMinutesTooLow
    | StudentIsNotTrainer
    | StudentDoesNotBelongInAcademy
    | FieldLengthNotValid
    | InsufficientPermissionsExam
    | InsufficientPermissionsOpposition;

  type ErrorWrapper = {
    error: ErrorTypes;
  };

  // ---------------------------------------------------------------------------------------------------------------------
  // 400 - Bad Request Error
  // ---------------------------------------------------------------------------------------------------------------------

  /* Start date is greater than the end date */
  type StartDateLaterThanFinishDate = Error<
    400,
    CreateChallengeErrorCodes.StartDateLaterThanFinishDate,
    ErrorTargets.Challenge
  >;

  /* Start date is lower than the current date */
  type StartDateLessThanNow = Error<400, CreateChallengeErrorCodes.StartDateLessThanNow, ErrorTargets.Challenge>;

  /* Challenge visibility|type is not valid (can only be public or private) */
  type InvalidChallengeVisibility = Error<
    400,
    CreateChallengeErrorCodes.InvalidChallengeVisibility,
    ErrorTargets.ChallengeVisibility
  >;

  /* Challenge creator type is not valid (can only be student, trainer or academy) */
  type InvalidChallengeCreatorType = Error<
    400,
    CreateChallengeErrorCodes.InvalidChallengeCreatorType,
    ErrorTargets.ChallengeCreatorType
  >;

  /* Challenge duration (difference between the start and end dates) exceeds the allowed duration */
  type DurationTooLong = Error<400, CreateChallengeErrorCodes.DurationTooLong, ErrorTargets.Challenge> & {
    maxDurationInMinutes: number;
  };

  /* Challenge duration (difference between the start and end dates) is minor than the allowed duration */
  type DurationTooShort = Error<400, CreateChallengeErrorCodes.DurationTooShort, ErrorTargets.Challenge> & {
    minDurationInMinutes: number;
  };

  /* The examId passed to create the challenges does not exist */
  type NotFound = Error<400, CreateChallengeErrorCodes.NotFound, ErrorTargets.Exam>;

  /* Request input is not valid */
  type BadRequest = Error<400, CreateChallengeErrorCodes.BadRequest, ErrorTargets.Input> & {
    details: {
      code: string;
      target: string;
      message: string;
    };
  };

  /* Challenge access time limit minutes cannot exceed the challenge duration */
  type AccessTimeLimitMinutesCannotExceedDuration = Error<
    400,
    CreateChallengeErrorCodes.AccessTimeLimitMinutesCannotExceedDuration,
    ErrorTargets.Challenge
  >;

  /* Challenge access time limit minutes cannot be less than minValue */
  type AccessTimeLimitMinutesTooLow = Error<
    400,
    CreateChallengeErrorCodes.AccessTimeLimitMinutesTooLow,
    ErrorTargets.Challenge
  > & {
    minValue: number;
  };

  /* The student must be registered as a trainer */
  type StudentIsNotTrainer = Error<400, CreateChallengeErrorCodes.StudentIsNotTrainer, ErrorTargets.Student>;

  /* The student must be registered as part of an academy */
  type StudentDoesNotBelongInAcademy = Error<
    400,
    CreateChallengeErrorCodes.StudentDoesNotBelongInAcademy,
    ErrorTargets.Student
  >;

  /* Field has not valid length */
  type FieldLengthNotValid = Error<400, CreateChallengeErrorCodes.FieldLengthNotValid, ErrorTargets.Challenge> & {
    field: 'title' | 'description';
  };

  // ---------------------------------------------------------------------------------------------------------------------
  // 403 - Insufficient Permissions Error
  // ---------------------------------------------------------------------------------------------------------------------

  /** Student has not permission to use this exam template */
  type InsufficientPermissionsExam = Error<403, CreateChallengeErrorCodes.InsufficientPermissions, ErrorTargets.Exam>;

  /** Student has not permission to create a paid test for this opposition */
  type InsufficientPermissionsOpposition = Error<
    403,
    CreateChallengeErrorCodes.InsufficientPermissions,
    ErrorTargets.Opposition
  >;
}
